<script lang="ts">
  import confetti from 'canvas-confetti';
  import Avatar from './Avatar.svelte';

  type Who = { emoji: string; name: string };
  let { emoji, name, others = [], word = 'BANGER!', got = 'got a line!', caption = '', celebrate = true }: {
    emoji: string;
    name: string;
    /** Further winners sharing the celebration (Album simultaneous finishers). */
    others?: Who[];
    /** The big word; 'PLATINUM!' is silver, 'THE REMIX!' cyan. */
    word?: 'BANGER!' | 'THE REMIX!' | 'PLATINUM!';
    /** Line under the winner(s). */
    got?: string;
    /** Small line under that (e.g. points won). */
    caption?: string;
    /** Fire confetti bursts (skipped under prefers-reduced-motion). */
    celebrate?: boolean;
  } = $props();

  const all = $derived<Who[]>([{ emoji, name }, ...others]);
  const platinum = $derived(word === 'PLATINUM!');
  const remix = $derived(word === 'THE REMIX!');

  const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Neon party colors; no gold (gold is reserved for Decade squares).
  const COLORS = ['#ff3df2', '#7df9ff', '#9775fa', '#ffffff', '#ff8ef7'];
  const PLATINUM_COLORS = ['#ffffff', '#e6ecf5', '#b8c4d6', '#7df9ff', '#d9d2ff'];

  const REMIX_COLORS = ['#7df9ff', '#ffffff', '#b8f6ff', '#9775fa', '#ff8ef7'];

  $effect(() => {
    if (!celebrate || reduced) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const fire = (opts: confetti.Options) =>
      confetti({ colors: platinum ? PLATINUM_COLORS : remix ? REMIX_COLORS : COLORS, disableForReducedMotion: true, zIndex: 200, ...opts });
    const bursts: [number, () => void][] = [
      [250, () => fire({ particleCount: 160, spread: 100, startVelocity: 55, origin: { x: 0.5, y: 0.55 } })],
      [700, () => fire({ particleCount: 90, angle: 60, spread: 70, origin: { x: 0, y: 0.8 } })],
      [700, () => fire({ particleCount: 90, angle: 120, spread: 70, origin: { x: 1, y: 0.8 } })],
      [1500, () => fire({ particleCount: 140, spread: 160, startVelocity: 45, scalar: 1.2, origin: { x: 0.5, y: 0.3 } })],
      [2600, () => fire({ particleCount: 80, angle: 60, spread: 60, origin: { x: 0, y: 0.7 } })],
      [2600, () => fire({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1, y: 0.7 } })],
    ];
    for (const [at, f] of bursts) timers.push(setTimeout(f, at));
    return () => {
      for (const t of timers) clearTimeout(t);
      confetti.reset();
    };
  });
</script>

<div class="flash" class:platinum class:remix role="alert" aria-live="assertive">
  <div class="strobe" aria-hidden="true"></div>
  <div class="rays" aria-hidden="true"></div>
  <div class="content">
    <h1 class="wordmark word">{word}</h1>
    <div class="winners" class:many={all.length > 1}>
      {#each all as w, i (i)}
        <div class="winner">
          <span class="avatar"><Avatar emoji={w.emoji} size={all.length > 1 ? 96 : 140} /></span>
          <span class="name">{w.name}</span>
        </div>
      {/each}
    </div>
    <p class="got">{got}</p>
    {#if caption}<p class="caption">{caption}</p>{/if}
  </div>
</div>

<style>
  .flash {
    position: fixed; inset: 0; z-index: 50; overflow: hidden;
    display: flex; align-items: center; justify-content: center; text-align: center;
    background: radial-gradient(circle at 50% 45%, #ff3df2 0, #3a0a5e 45%, #0a0618 80%);
  }
  /* Pink strobe: 3 flashes per second at most (photosensitivity guideline), ~2.4 s, then a steady glow. */
  .strobe {
    position: absolute; inset: 0; background: #ff3df2; opacity: 0;
    animation: strobe 0.333s steps(1, end) 7, glow 1.2s ease-out 2.4s both;
  }
  .rays {
    position: absolute; left: 50%; top: 50%; width: 220vmax; height: 220vmax; margin: -110vmax 0 0 -110vmax;
    background: repeating-conic-gradient(rgba(255, 255, 255, 0.09) 0 6deg, transparent 6deg 18deg);
    animation: rays 18s linear infinite;
  }
  .content { position: relative; display: grid; justify-items: center; gap: 1vh; padding: 0 4vw; }
  .word {
    margin: 0; font-size: clamp(5rem, 19vw, 18rem); line-height: 1;
    animation: slam 0.9s cubic-bezier(0.2, 1.6, 0.4, 1) both, flicker 6s 1s infinite;
  }
  .winners { display: flex; flex-wrap: wrap; justify-content: center; gap: 1.5vh 4vw; max-width: 92vw; }
  .winner { display: flex; align-items: center; gap: 2vw; animation: rise 0.6s 0.7s ease-out both; }
  .many .name { font-size: clamp(2rem, 5vw, 4.4rem); max-width: 9ch; }
  .avatar { display: inline-flex; animation: bob 1.2s 1.3s ease-in-out infinite alternate; }
  .name {
    font-size: clamp(3rem, 8vw, 7rem); font-weight: 900; color: #fff; max-width: 12ch;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    /* A dark outline first so the name reads on hot pink, then the cyan glow. The glow is a
       filter (it follows the glyphs) because overflow:hidden would clip text-shadow to a box. */
    padding: 0.08em 0.12em;
    -webkit-text-stroke: 2px #0a0618; paint-order: stroke fill;
    text-shadow: 0 3px 0 #0a0618, 0 0 5px #0a0618;
    filter: drop-shadow(0 0 10px var(--cyan)) drop-shadow(0 0 22px var(--cyan));
    opacity: 1;
  }
  .got { margin: 0; font-size: clamp(1.6rem, 3.5vw, 3rem); font-weight: 800; animation: rise 0.6s 1s ease-out both; }
  .caption { margin: 0; white-space: pre-line; font-size: clamp(1.2rem, 2.2vw, 2rem); text-shadow: 0 0 8px #0a0618; animation: rise 0.6s 1.2s ease-out both; }

  /* PLATINUM!: same choreography, silver/white-platinum instead of hot pink. */
  .platinum { background: radial-gradient(circle at 50% 42%, #c9d2e0 0, #626a82 24%, #252a40 52%, #0a0618 82%); }
  .platinum .strobe { background: #ffffff; }
  .platinum .rays { background: repeating-conic-gradient(rgba(220, 232, 255, 0.07) 0 6deg, transparent 6deg 18deg); }
  .platinum .word {
    font-size: clamp(4rem, 13vw, 13rem); /* nine letters: keep it inside the screen */
    /* Glow via filter: a text-shadow would show through the transparent, gradient-clipped glyphs. */
    text-shadow: none;
    background: linear-gradient(180deg, #ffffff 0%, #e9eef6 45%, #a9b4c6 55%, #ffffff 100%);
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 3px 0 #2b2f45) drop-shadow(0 0 12px rgba(255, 255, 255, 0.9)) drop-shadow(0 0 34px rgba(200, 225, 255, 0.75));
  }
  .platinum .got, .platinum .caption { text-shadow: 0 0 8px #0a0618, 0 2px 0 #0a0618; }

  /* THE REMIX!: same choreography, cyan accent instead of hot pink. */
  .remix { background: radial-gradient(circle at 50% 45%, #7df9ff 0, #0b5a78 40%, #0a0618 80%); }
  .remix .strobe { background: #7df9ff; }
  .remix .word {
    font-size: clamp(4rem, 14vw, 14rem); /* ten characters: keep it inside the screen */
    text-shadow: 0 0 12px #7df9ff, 0 0 34px #7df9ff, 0 0 60px rgba(125, 249, 255, 0.6);
  }
  .remix .got, .remix .caption { text-shadow: 0 0 8px #0a0618, 0 2px 0 #0a0618; }

  @keyframes strobe { 0% { opacity: 0.85; } 50% { opacity: 0; } }
  @keyframes glow { from { opacity: 0.6; } to { opacity: 0.12; } }
  @keyframes rays { to { transform: rotate(360deg); } }
  @keyframes slam {
    0% { transform: scale(0.1) rotate(-12deg); opacity: 0; }
    60% { transform: scale(1.18) rotate(3deg); opacity: 1; }
    100% { transform: scale(1) rotate(0); opacity: 1; }
  }
  @keyframes rise { from { transform: translateY(40px); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes bob { to { transform: translateY(-14px) rotate(6deg); } }

  /* Reduced motion: no strobe or movement, just one calm fade of a soft pink glow. */
  @keyframes calm { from { opacity: 0; } to { opacity: 0.3; } }
  @media (prefers-reduced-motion: reduce) {
    .rays { display: none; }
    .flash .strobe { animation: calm 1.5s ease-out both !important; animation-duration: 1.5s !important; }
  }
</style>
